// Maps game slugs to locally-hosted logo URLs in the game-logos storage bucket
// These are fallbacks; the database logo_url field takes priority

const STORAGE_BASE = "https://spugyzexlkzybtkuhchf.supabase.co/storage/v1/object/public/game-logos";

/** Lowercase + collapse non-alphanumerics to hyphens (matches the CDN's naming). */
function slugifyName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Slugs we know don't exist on the CDN under their natural name, mapped to one that does. */
const SLUG_ALIASES: Record<string, string> = {
  "powerball-double-play": "powerball",
  "mega-millions-just-the-jackpot": "mega-millions",
};

export const GAME_LOGOS: Record<string, string> = {
  "powerball": `${STORAGE_BASE}/powerball.svg`,
  "powerball-double-play": `${STORAGE_BASE}/powerball.svg`,
  "mega-millions": `${STORAGE_BASE}/mega-millions.svg`,
  "lucky-for-life": `${STORAGE_BASE}/lucky-for-life.svg`,
  "cash4life": `${STORAGE_BASE}/cash4life.svg`,
  "lotto-america": `${STORAGE_BASE}/lotto-america.svg`,
  "cash-5": `${STORAGE_BASE}/cash-5.svg`,
  "pick-3": `${STORAGE_BASE}/pick-3.svg`,
  "pick-4": `${STORAGE_BASE}/pick-4.svg`,
};

// Known fixed-prize games (not progressive jackpots)
export const FIXED_PRIZE_GAMES: Record<string, { amount: string; label: string }> = {
  "powerball-double-play": { amount: "$10,000,000", label: "Top Prize" },
  "lucky-for-life": { amount: "$1,000/day for life", label: "Top Prize" },
  "cash4life": { amount: "$1,000/day for life", label: "Top Prize" },
};

/**
 * Resolve a CDN logo URL. Prefers the explicit GAME_LOGOS map, then derives a
 * slug from the game's display name (or the slug itself, stripping any
 * state-name prefix), and finally returns null if neither is available.
 */
export function getGameLogoUrl(slug: string, gameName?: string): string | null {
  if (GAME_LOGOS[slug]) return GAME_LOGOS[slug];
  if (SLUG_ALIASES[slug]) return `${STORAGE_BASE}/${SLUG_ALIASES[slug]}.svg`;

  // Try the slugified game-name (matches the CDN's file naming for ~all games).
  if (gameName) {
    const nameSlug = slugifyName(gameName);
    if (nameSlug) return `${STORAGE_BASE}/${nameSlug}.svg`;
  }

  // Fall back to the raw slug, with any state-name prefix stripped.
  // e.g. "arizona-the-pick" → "the-pick"
  const cleaned = slug.replace(/^[a-z]+(?:-[a-z]+)*-(?=[a-z0-9])/, (m) => {
    // Only strip if the remainder looks like a real game slug (>= 3 chars)
    return m;
  });
  return `${STORAGE_BASE}/${cleaned}.svg`;
}

// Generate a deterministic color from a string
export function slugToColor(slug: string): string {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = slug.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  // Lightness 28% keeps white text above WCAG AA (4.5:1) at every hue,
  // including yellow/green/cyan where higher lightness previously failed.
  return `hsl(${h}, 65%, 28%)`;
}

export function getGameInitials(name: string): string {
  return name
    .split(/\s+/)
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
